<!--
  Notices shown in the chatbot. Currently all types of notices uses the same component.
  While this suffices our base use cases, if we need extreme customization of the notices,
  you should create a copy of this component, and add a type of notice in the `NoticeType`
  enum. Then this component can render separately with the others.
-->
<script setup lang="ts">
import { computed, type CSSProperties } from 'vue';

import PlsText from '@/components/PlsText.vue';
import { ItemType } from '@/library/message/lib';
import type { BaseNotice } from '@/library/message/types';
import { useNoticeCustomisation } from '@/components/chat/composables/useNoticeCustomisation';

export interface ChatNoticeProps {
  notice: BaseNotice;
}

const props = defineProps<ChatNoticeProps>();

const {
  infoNoticeTextColor,
  infoNoticeBackgroundColor,
  warningNoticeTextColor,
  warningNoticeBackgroundColor,
  errorNoticeTextColor,
  errorNoticeBackgroundColor,
} = useNoticeCustomisation();

const noticeType = computed(() => {
  const notice = props.notice;
  if (ItemType.isInfoLikeNotice(notice)) return 'info';
  if (ItemType.isWarningLikeNotice(notice)) return 'warning';
  if (ItemType.isErrorLikeNotice(notice)) return 'error';
  return 'info';
});

const noticeStyles = computed<CSSProperties>(() => {
  switch (noticeType.value) {
    case 'warning':
      return {
        backgroundColor: warningNoticeBackgroundColor.value,
        color: warningNoticeTextColor.value,
      };
    case 'error':
      return {
        backgroundColor: errorNoticeBackgroundColor.value,
        color: errorNoticeTextColor.value,
      };
    case 'info':
    default:
      return {
        backgroundColor: infoNoticeBackgroundColor.value,
        color: infoNoticeTextColor.value,
      };
  }
});

const iconColor = computed(() => {
  return noticeStyles.value.color;
});

const isLargeNotice = computed(() => (props.notice.message || '').length > 40);
</script>

<template>
  <div class="notice-container" data-testid="chat-notice" :data-notice-type="notice.type">
    <PlsText class="notice-content shadow-soft" :class="{ 'notice-large': isLargeNotice }" :style="noticeStyles">
      <v-icon v-if="notice.icon" :name="notice.icon" class="notice-icon" :style="{ color: iconColor }" />
      <PlsText>{{ notice.message }}</PlsText>
    </PlsText>
  </div>
</template>

<style scoped>
.notice-container {
  text-align: center;
  margin-bottom: 16px;
}

.notice-content {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 9999px;
}

.notice-content.notice-large {
  border-radius: 0.5rem;
}

.notice-icon {
  margin-right: 0.5rem;
}
</style>
